import { useNavigate } from 'react-router-dom';
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, PieChart, Pie, Cell, ReferenceDot,
} from 'recharts';
import { Coins, CircleDot, TrendingUp, CalendarClock } from 'lucide-react';
import { PanelImage } from '../../lib/panelImages';

const fmt = (n) => new Intl.NumberFormat('fr-FR').format(Math.round(n || 0)).replace(/\u202f|,/g, ' ');
const card = 'bg-white rounded-2xl shadow-[0_2px_12px_rgba(15,23,42,0.06)]';
const TYPE_COLORS = { led: '#2f5be8', deroulant: '#f3a25a', grand_format: '#12b981', poteau: '#8b5cf6' };

export function KpiGradient({ title, value, sub, from, to, testid, delay = 0 }) {
  return (
    <div
      className="relative overflow-hidden rounded-2xl px-5 py-4 text-white cs-rise"
      style={{ background: `linear-gradient(120deg, ${from}, ${to})`, animationDelay: `${delay}ms` }}
      data-testid={testid}
    >
      <div className="absolute -top-10 -right-6 w-28 h-28 rounded-full bg-white/10" />
      <div className="relative">
        <div className="text-sm font-medium text-white/90">{title}</div>
        <div className="text-3xl font-extrabold tracking-tight leading-tight" data-testid={`${testid}-value`}>{value}</div>
        <div className="text-xs text-white/85">{sub}</div>
      </div>
    </div>
  );
}

function EvoTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#0f1a3a] text-white text-xs rounded-xl px-3 py-2 shadow-lg">
      <div className="font-bold mb-1">{label}</div>
      {payload.map((p) => <div key={p.dataKey}>{p.name} : <b>{p.value}</b></div>)}
    </div>
  );
}

export function EvolutionCard({ data }) {
  const evo = data.evolution || [];
  const peak = data.peak;
  return (
    <div className={`${card} p-6`} data-testid="dash-evolution">
      <h3 className="text-lg font-bold text-[#0f1a3a]">Évolution mensuelle</h3>
      <p className="text-slate-500 mt-1">Prospects, contrats et clients depuis {evo[0]?.mois?.replace(/^Jan/, 'janvier') || 'janvier'}</p>
      <div className="flex gap-6 mt-4 text-sm text-slate-700">
        {[['#2f5be8', 'Prospects'], ['#f3a25a', 'Contrats'], ['#12b981', 'Clients']].map(([c, l]) => (
          <span key={l} className="flex items-center gap-2"><span className="w-3.5 h-3.5 rounded" style={{ background: c }} />{l}</span>
        ))}
      </div>
      <div className="h-[300px] mt-2">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={evo} margin={{ top: 30, right: 20, left: -10, bottom: 0 }}>
            <defs>
              <linearGradient id="gProspects" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#2f5be8" stopOpacity={0.28} />
                <stop offset="100%" stopColor="#2f5be8" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="4 6" vertical={false} stroke="#e2e8f0" />
            <XAxis dataKey="mois" tickLine={false} axisLine={false} tick={{ fill: '#64748b', fontSize: 12 }} />
            <YAxis tickLine={false} axisLine={false} tick={{ fill: '#64748b', fontSize: 12 }} allowDecimals={false} />
            <Tooltip content={<EvoTooltip />} />
            <Area type="monotone" dataKey="prospects" name="Prospects" stroke="#2f5be8" strokeWidth={3} fill="url(#gProspects)" dot={{ r: 5, fill: '#fff', strokeWidth: 3 }} />
            <Area type="monotone" dataKey="contrats" name="Contrats" stroke="#f3a25a" strokeWidth={3} fill="transparent" dot={{ r: 5, fill: '#fff', strokeWidth: 3 }} />
            <Area type="monotone" dataKey="clients" name="Clients" stroke="#12b981" strokeWidth={3} fill="transparent" dot={{ r: 5, fill: '#fff', strokeWidth: 3 }} />
            {peak?.prospects > 0 && (
              <ReferenceDot
                x={peak.mois} y={peak.prospects} r={0}
                label={{ value: `Pic : ${peak.prospects} prospects`, position: 'top', fill: '#0f1a3a', fontSize: 13, fontWeight: 700, offset: 14 }}
              />
            )}
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export function ParcoursCard({ data }) {
  const peak = data.peak;
  const rows = [
    ['Prospects', data.total_prospects, '#2f5be8', '100%'],
    ['Clients', data.total_clients, '#2f5be8', '62%'],
    ['Contrats', data.contrats_actifs, '#2f5be8', '48%'],
  ];
  const taux = String(data.taux_conversion).replace('.', ',');
  return (
    <div className="rounded-2xl p-6 text-white bg-[#121f6b] flex flex-col" data-testid="dash-parcours">
      <h3 className="text-lg font-bold">Parcours commercial</h3>
      <p className="text-white/70 mt-1">De la prospection au contrat</p>
      <div className="mt-4 space-y-2.5 flex flex-col items-center">
        {rows.map(([l, v, c, w]) => (
          <div key={l} className="h-11 rounded-xl px-4 flex items-center justify-between font-bold text-base transition-transform hover:scale-[1.02]" style={{ background: c, width: w }} data-testid={`parcours-${l.toLowerCase()}`}>
            <span>{l}</span><span className="text-lg">{v}</span>
          </div>
        ))}
      </div>
      <div className="border-t border-white/15 mt-5 pt-4 text-sm leading-relaxed text-white/90" data-testid="parcours-insight">
        <b className="text-white">À retenir :</b>{' '}
        {peak?.prospects
          ? <>le pic de {peak.mois} ({peak.prospects} prospects) {peak.clients < 2 ? 'a peu converti' : `a donné ${peak.clients} clients`}. Relancer les prospects de ce mois peut faire passer le taux au-dessus de {taux} %.</>
          : <>ajoutez vos premiers prospects pour suivre votre taux de conversion.</>}
      </div>
    </div>
  );
}

function PanelVisual({ k, label }) {
  return (
    <div className="w-full" data-testid={`panel-visual-${k}`}>
      <span className="inline-block bg-white text-[#0f1a3a] text-xs font-bold px-3 py-1 rounded-full shadow-sm mb-2">{label}</span>
      <div className="w-full h-32 rounded-xl bg-white flex items-center justify-center overflow-hidden">
        <PanelImage type={k} alt={label} className="h-full w-full p-2 transition-transform duration-500 hover:scale-105" />
      </div>
    </div>
  );
}

export function NosPanneauxCard({ data }) {
  const navigate = useNavigate();
  const types = data.panneaux || [];
  return (
    <div className={`${card} p-6`} data-testid="dash-nos-panneaux">
      <h3 className="text-lg font-bold text-[#0f1a3a]">Nos panneaux SESAM</h3>
      <p className="text-slate-500 mt-1">{data.total_panneaux} faces en exploitation · revenu potentiel par type</p>
      <div className={`grid gap-5 mt-6 grid-cols-1 sm:grid-cols-2 ${types.length >= 4 ? 'xl:grid-cols-4' : 'xl:grid-cols-3'}`}>
        {types.map((t) => (
          <div key={t.key} className="bg-slate-50/70 rounded-2xl p-4 transition-transform hover:-translate-y-1" data-testid={`panel-type-${t.key}`}>
            <PanelVisual k={t.key} label={t.label} />
            <div className="mt-2 flex items-baseline gap-3">
              <span className="text-3xl font-extrabold text-[#0f1a3a]">{t.count}</span>
              <span className="text-lg font-semibold text-slate-500">panneau{t.count > 1 ? 'x' : ''}</span>
            </div>
            <div className="h-2 rounded-full bg-slate-200 mt-3 overflow-hidden">
              <div className="h-full rounded-full transition-[width] duration-700" style={{ width: `${Math.max(3, t.share_pct)}%`, background: TYPE_COLORS[t.key] }} />
            </div>
            <div className="flex justify-between mt-2 text-sm">
              {t.prix_jour
                ? <><span className="text-slate-500">{fmt(t.prix_jour)} FCFA / jour</span><span className="font-bold text-[#0f1a3a]" data-testid={`panel-total-${t.key}`}>{fmt(t.total_jour)}</span></>
                : <button onClick={() => navigate('/tarifs')} className="text-blue-700 font-semibold hover:underline" data-testid={`panel-set-tarif-${t.key}`}>Tarif à définir →</button>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function RevenuTotalCard({ data }) {
  return (
    <div className={`${card} p-6`} data-testid="dash-revenu-total">
      <h3 className="text-lg font-bold text-[#0f1a3a]">Revenu total / jour</h3>
      <p className="text-slate-500 text-sm">Si tous les panneaux sont loués</p>
      <div className="text-3xl font-extrabold mt-4 tracking-tight text-[#0f1a3a]" data-testid="dash-revenu-jour">{fmt(data.revenu_jour)} <span className="text-base text-slate-500">FCFA</span></div>
      <div className="border-t border-slate-100 mt-4 pt-4 space-y-2 text-sm text-slate-700">
        <div className="flex justify-between"><span>Projection mensuelle</span><span className="font-bold text-emerald-600" data-testid="dash-proj-mois">{fmt(data.projection_mensuelle)}</span></div>
        <div className="flex justify-between"><span>Projection annuelle</span><span className="font-bold text-emerald-600" data-testid="dash-proj-an">{fmt(data.projection_annuelle)}</span></div>
      </div>
    </div>
  );
}

export function SmallKpis({ data }) {
  const actifs = data.contrats_actifs;
  const items = [
    [Coins, 'bg-indigo-50 text-indigo-600', fmt(data.moyenne_panneau_jour), 'FCFA / panneau / jour (moyenne)', 'kpi-moyenne'],
    [CircleDot, 'bg-emerald-50 text-emerald-600', `${data.panneaux_disponibles} / ${data.panneaux_total_faces}`, 'Panneaux disponibles à la vente', 'kpi-disponibles'],
    [TrendingUp, 'bg-orange-50 text-orange-500', `${data.top_type?.share_pct ?? 0} %`, `Du revenu vient du ${data.top_type?.label || '—'}`, 'kpi-top-type'],
    [CalendarClock, 'bg-rose-50 text-rose-500', `${fmt(data.montant_a_venir)} FCFA`, `Montant à venir · ${actifs ? `${actifs} contrat${actifs > 1 ? 's' : ''} actif${actifs > 1 ? 's' : ''}` : 'aucun contrat actif'}`, 'kpi-a-venir'],
  ];
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
      {items.map(([Icon, tint, v, l, id], i) => (
        <div key={id} className={`${card} p-5 cs-rise`} style={{ animationDelay: `${i * 60}ms` }} data-testid={id}>
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${tint}`}><Icon className="h-4 w-4" /></div>
          <div className="text-2xl font-extrabold text-[#0f1a3a] mt-3" data-testid={`${id}-value`}>{v}</div>
          <div className="text-slate-500 text-sm">{l}</div>
        </div>
      ))}
    </div>
  );
}

export function RevenusParType({ data }) {
  const rows = (data.panneaux || []).filter((t) => t.total_jour > 0);
  return (
    <div className={`${card} p-6`} data-testid="dash-revenus-type">
      <h3 className="text-lg font-bold text-[#0f1a3a]">Revenus par type</h3>
      <p className="text-slate-500 mt-1">Répartition du revenu potentiel / jour</p>
      {rows.length === 0 ? <p className="text-sm text-slate-400 mt-8">Aucun tarif défini pour vos panneaux.</p> : (
        <div className="flex items-center gap-6 mt-4">
          <div className="w-48 h-48 flex-shrink-0">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={rows} dataKey="total_jour" nameKey="label" innerRadius="58%" outerRadius="100%" startAngle={90} endAngle={-270} stroke="none">
                  {rows.map((t) => <Cell key={t.key} fill={TYPE_COLORS[t.key]} />)}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="space-y-3">
            {rows.map((t) => (
              <div key={t.key} className="flex items-center gap-3 text-[15px] text-slate-800">
                <span className="w-3.5 h-3.5 rounded" style={{ background: TYPE_COLORS[t.key] }} />{t.label} · {t.share_pct} %
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function DistributionContrats({ data }) {
  const navigate = useNavigate();
  const dist = data.distribution_contrats || [];
  const total = dist.reduce((s, d) => s + d.count, 0);
  const palette = ['#2f5be8', '#f3a25a', '#12b981', '#8b5cf6', '#94a3b8'];
  return (
    <div className={`${card} p-6`} data-testid="dash-distribution">
      <h3 className="text-lg font-bold text-[#0f1a3a]">Distribution des contrats</h3>
      <p className="text-slate-500 mt-1">{total ? `${total} contrat${total > 1 ? 's' : ''} en cours` : 'Aucun contrat en cours'}</p>
      {total === 0 ? (
        <div className="mt-5 border-2 border-dashed border-slate-200 rounded-2xl p-6 text-center">
          <p className="text-slate-600">Vos {data.total_panneaux} panneaux attendent leur premier client.</p>
          <button onClick={() => navigate('/contrats')} className="mt-3 px-6 h-11 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-bold transition-colors" data-testid="dash-create-contrat">Créer un contrat</button>
        </div>
      ) : (
        <div className="mt-6 space-y-4">
          <div className="flex h-4 rounded-full overflow-hidden gap-1">
            {dist.map((d, i) => <div key={d.label} style={{ flex: d.count, background: palette[i % palette.length] }} />)}
          </div>
          {dist.map((d, i) => (
            <div key={d.label} className="flex items-center justify-between text-[15px]" data-testid={`dist-${d.label}`}>
              <span className="flex items-center gap-3 text-slate-800"><span className="w-3.5 h-3.5 rounded" style={{ background: palette[i % palette.length] }} />{d.label}</span>
              <span className="font-bold text-[#0f1a3a]">{d.count} · {Math.round((d.count / total) * 100)} %</span>
            </div>
          ))}
          <button onClick={() => navigate('/contrats')} className="text-sm font-semibold text-blue-700 hover:underline">Voir les contrats →</button>
        </div>
      )}
    </div>
  );
}

export function ProspectsParStatut({ data }) {
  const rows = data.prospects_par_statut || [];
  const max = Math.max(1, ...rows.map((r) => r.count));
  const palette = ['#2f5be8', '#7c3aed', '#f3a25a', '#0ea5e9', '#12b981'];
  return (
    <div className={`${card} p-6`} data-testid="dash-prospects-statut">
      <h3 className="text-lg font-bold text-[#0f1a3a]">Prospects par statut</h3>
      <p className="text-slate-500 mt-1">Pipeline commercial</p>
      <div className="mt-5 space-y-4">
        {rows.map((r, i) => (
          <div key={r.statut} className="grid grid-cols-[120px_1fr_40px] items-center gap-4" data-testid={`statut-${r.statut}`}>
            <span className="text-[15px] text-slate-800 truncate">{r.statut}</span>
            <div className="h-3.5 rounded-full bg-slate-100 overflow-hidden">
              <div className="h-full rounded-full transition-[width] duration-700" style={{ width: `${(r.count / max) * 100}%`, background: r.statut === 'Gagné' ? '#12b981' : palette[i % palette.length] }} />
            </div>
            <span className="font-bold text-[#0f1a3a] text-right">{r.count}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
